import { isValid, parseISO, startOfDay } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

export const DateFormat = {
  ISO_DATE: 'yyyy-MM-dd',
  MONTH_SHORT_DAY_SHORT: 'MMM d',
  MONTH_SHORT_DAY_SHORT_YEAR: 'MMM d, yyyy',
  DAY_SHORT_MONTH_SHORT_YEAR: 'dd MMM, yyyy',
  WEEKDAY_MONTH_SHORT_DAY_SHORT_YEAR: 'eee MMM d, yyyy',
  MONTH_LONG: 'MMMM',
  MONTH_SHORT: 'MMM',
  MONTH_NUMBER: 'MM',
  DAY_OF_MONTH: 'd',
  DAY_NUMBER: 'dd',
  FULL_YEAR: 'yyyy',
  MONTH_LONG_DAY_SHORT_YEAR: 'MMMM d, yyyy',
  MONTH_LONG_DAY_SHORT_YEAR_TIME: 'MMMM d, yyyy h:mm a',
  ISO_SHORT_TIME: 'h:mm a',
  MILITARY_TIME: 'HH:mm',
  NUMERIC_DATE: 'MM/dd/yyyy',
  DAY_MONTH_YEAR: 'dd/MM/yyyy',
  DAY_MONTH: 'dd/MM',
} as const;

const getDateFormatString = (locale: string) => {
  const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());

  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return DateFormat.DAY_NUMBER;
        case 'month':
          return DateFormat.MONTH_NUMBER;
        case 'year':
          return DateFormat.FULL_YEAR;
        default:
          return obj.value;
      }
    })
    .join('');
};

const getLocaleHelpers = () => {
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
  const localeFormatStr = getDateFormatString(locale);

  return { timeZone, localeFormatStr };
};

/**
 * @name formatLocalDate
 * @summary Format ISO Date String to browser locale format
 *
 * @param dateString the date in ISO String format
 * @param formatStr(optional) the format to use.  Will default to browser locale if not provided
 *
 * @example
 * // Given September 8, 2025 08:38 am (in Seattle, WA USA (US Pacific))
 * const result = formatLocalDate('2025-09-08T15:38:30.000Z')
 * //=> "09/08/2025"
 *
 * // Given September 8, 2025 08:38 am (in Seattle, WA USA (US Pacific)), formatted for German
 * const result = formatLocalDate('2025-09-08T15:38:30.000Z')
 * //=> "08.09.2025"
 *
 */
export const formatLocalDate = (dateString: string | Date, formatStr?: string) => {
  return formatLocalDateWithFallback({ dateString, formatStr, fallback: '-' });
};

export const formatDateAsLocal = (dateString: string, formatStr?: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const { localeFormatStr } = getLocaleHelpers();
  const adjustedDate = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);

  return format(adjustedDate, formatStr ?? localeFormatStr);
};

export const formatLocalDateWithTime = (dateString: string, timeStr?: string) => {
  const { localeFormatStr } = getLocaleHelpers();
  return formatLocalDate(dateString, `${localeFormatStr} ${timeStr ?? DateFormat.ISO_SHORT_TIME}`);
};

export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const ISO_DATE_PREFIX_PATTERN = /^\d{4}-\d{2}-\d{2}/;

export const toISOTimestamp = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return parseISO(dateString).toISOString();
};

export const toISODateString = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  return iso.match(ISO_DATE_PREFIX_PATTERN)?.[0] ?? null;
};

/**
 * Reduces an ISO date or datetime to the start of the calendar day the UI shows
 * for it: a date-only string keeps its own day, a datetime resolves in the
 * viewer's timezone the same way `formatLocalDateWithFallback` renders it.
 * Comparing these keeps day-granular rules agreeing with the dates on screen.
 */
export const toDisplayedDayStart = (dateString: string | null | undefined): Date | undefined => {
  if (!dateString) return undefined;

  if (ISO_DATE_PATTERN.test(dateString)) return toLocalMidnight(dateString);

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return undefined;

  const { timeZone } = getLocaleHelpers();
  return startOfDay(toZonedTime(date, timeZone));
};

export const parseDateValue = (value: string | Date): Date | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  if (ISO_DATE_PATTERN.test(value)) {
    return toLocalMidnight(value);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const formatLocalDateWithFallback = ({
  dateString,
  formatStr,
  fallback = '',
}: {
  dateString?: string | Date | null;
  formatStr?: string;
  fallback?: string;
}) => {
  if (!dateString) {
    return fallback;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  const { timeZone, localeFormatStr } = getLocaleHelpers();

  if (typeof dateString === 'string' && ISO_DATE_PATTERN.test(dateString)) {
    const utcDate = toZonedTime(date, 'UTC');
    return format(utcDate, formatStr ?? localeFormatStr, { timeZone: 'UTC' });
  }

  const zonedDate = toZonedTime(date, timeZone);

  return format(zonedDate, formatStr ?? localeFormatStr, { timeZone });
};

/**
 * Creates a Date at UTC midnight for the given year, month, and day.
 * @param year - The full year (e.g., 2025)
 * @param month - The month (0-11, where 0 = January, 11 = December)
 * @param day - The day of the month (1-31)
 */
export const toUTCMidnight = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
};

/**
 * Normalizes a date to UTC midnight, preserving the calendar date in UTC timezone.
 */
export const dateToUTCMidnight = (date: Date): Date => {
  return toUTCMidnight(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Milliseconds until the next UTC calendar day begins, for scheduling a refresh exactly when a
 * value derived from "today" in UTC (e.g. a backend as-of-today calculation) can change.
 */
export const msUntilNextUTCMidnight = (now: Date = new Date()): number => {
  const nextUTCMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return nextUTCMidnight - now.getTime();
};

/**
 * Converts a UTC midnight date string to a Date at local midnight with the same calendar date.
 * Used for displaying date-only values in Calendar components without timezone shifts.
 */
export const toLocalMidnight = (dateString: string): Date | undefined => {
  const parsedDate = fromDateString(dateString);
  if (!parsedDate) return undefined;
  return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
};

/**
 * Parses a date string (YYYY-MM-DD or ISO format) as UTC midnight, avoiding timezone shifts.
 * Returns null for invalid dates (e.g., 2025-02-31).
 */
export const fromDateString = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;

  const prefix = dateStr.match(ISO_DATE_PREFIX_PATTERN)?.[0];
  if (!prefix) return null;

  const [yearStr = '', monthStr = '', dayStr = ''] = prefix.split('-');
  const year = Number.parseInt(yearStr, 10);
  const month = Number.parseInt(monthStr, 10);
  const day = Number.parseInt(dayStr, 10);

  const date = toUTCMidnight(year, month - 1, day);

  if (
    !isValid(date) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
};

// Value is irrelevant — only the arrangement of the month/day parts is read.
const PART_ORDER_REFERENCE_DATE = new Date(Date.UTC(2026, 0, 5));

const isMonthOrDayPart = (
  part: Intl.DateTimeFormatPart,
): part is Intl.DateTimeFormatPart & { type: 'month' | 'day' } =>
  part.type === 'month' || part.type === 'day';

const datePartOrderCache = new Map<string, ('month' | 'day')[]>();

// Derives whether the month or the day comes first from the locale's FULL-date
// pattern — the same source `getDateFormatString` uses for the table columns —
// so headers can't disagree with the tables. A month+day-only skeleton is not
// safe here: for e.g. sv-SE the full pattern is yyyy-MM-dd (month before day)
// while the skeleton resolves day before month.
const getDatePartOrder = (locale: string): ('month' | 'day')[] => {
  const cached = datePartOrderCache.get(locale);
  if (cached) return cached;
  const order = new Intl.DateTimeFormat(locale)
    .formatToParts(PART_ORDER_REFERENCE_DATE)
    .filter(isMonthOrDayPart)
    .map((part) => part.type);
  datePartOrderCache.set(locale, order);
  return order;
};

const dayMonthFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getDayMonthFormatter = (locale: string): Intl.DateTimeFormat => {
  const cached = dayMonthFormatterCache.get(locale);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
    // Pin the calendar and digits to match the date-fns table path (always
    // Gregorian + ASCII). Without this, fa-IR renders a Persian-calendar date
    // that doesn't line up with the Gregorian/UTC column geometry, and
    // ar-EG/ne-NP render non-ASCII digits.
    calendar: 'gregory',
    numberingSystem: 'latn',
  });
  dayMonthFormatterCache.set(locale, formatter);
  return formatter;
};

/**
 * @name formatDayMonth
 * @summary Format a UTC date as a locale-ordered day/month pair (no year).
 *
 * The day-first vs month-first ordering follows the browser locale — derived
 * from the same full-date pattern as {@link formatLocalDate}, so it matches the
 * ordering of the table date columns. The two parts are re-joined with a fixed
 * `/` so the width stays predictable and no locale-specific punctuation
 * (trailing dots, extra spaces) leaks into the compact timeline/planner labels.
 * Formatters are cached per locale since this runs twice per header cell
 * (rangeStart + rangeEnd) on every column rebuild.
 */
export const formatDayMonth = (date: Date): string => {
  const { locale } = Intl.DateTimeFormat().resolvedOptions();
  const values: Partial<Record<'month' | 'day', string>> = {};
  for (const part of getDayMonthFormatter(locale).formatToParts(date)) {
    if (isMonthOrDayPart(part)) values[part.type] = part.value;
  }
  return getDatePartOrder(locale)
    .map((type) => values[type] ?? '')
    .join('/');
};

export const formatDateNumeric = (dateStr: string): string => {
  const date = fromDateString(dateStr);
  if (!date) return '';
  return formatDayMonth(date);
};

const relativeTimeFormatters = {
  narrow: new Intl.RelativeTimeFormat(undefined, { numeric: 'always', style: 'narrow' }),
  long: new Intl.RelativeTimeFormat(undefined, { numeric: 'always', style: 'long' }),
};

export function formatRelativeTime(dateStr: string, style: 'narrow' | 'long' = 'narrow'): string {
  const formatter = relativeTimeFormatters[style];
  const diff = Date.now() - new Date(dateStr).getTime();
  // Server-written timestamps can sit ahead of the browser clock, which would otherwise
  // floor to -1 and render as a future time ("in 1m") for something that just happened.
  const minutes = Math.max(0, Math.floor(diff / 60000));
  if (minutes < 60) return formatter.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return formatter.format(-hours, 'hour');
  const days = Math.floor(hours / 24);
  return formatter.format(-days, 'day');
}
